// src/tracks/midiLoader.js
// Import/export MIDI files using @tonejs/midi.
//
// Import: parses a .mid file, returns an array of tracks worth of note data.
// Export: builds a MIDI file from the current arrangement and triggers a download.

import { Midi } from '@tonejs/midi';

/**
 * Parse a MIDI File into a list of { name, notes } tracks where notes
 * are { pitch, time, duration, velocity } in seconds.
 */
export function parseMidiFile(arrayBuffer) {
  const midi = new Midi(arrayBuffer);
  return midi.tracks.map((t) => ({
    name: t.name || 'Imported',
    notes: t.notes.map((n) => ({
      pitch: n.midi,            // MIDI number
      time: n.time,             // seconds
      duration: n.duration,     // seconds
      velocity: n.velocity      // 0..1
    }))
  }));
}

/**
 * Build a MIDI file from a list of { trackName, notes } and a tempo (BPM).
 * `tracks` is an array of Track instances from TrackManager.
 */
export function exportMidi(tracks, bpm = 100) {
  const midi = new Midi();
  midi.header.setTempo(bpm);

  for (const t of tracks) {
    const tMidi = midi.addTrack();
    tMidi.name = t.name || t.instrumentId;
    for (const n of t.notes) {
      tMidi.addNote({
        midi: n.pitch,
        time: n.time,
        duration: n.duration,
        velocity: n.velocity
      });
    }
  }
  return midi.toArray();
}

/**
 * Trigger a download of a Uint8Array as a .mid file.
 */
export function downloadMidi(bytes, filename = 'arrangement.mid') {
  const blob = new Blob([bytes], { type: 'audio/midi' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}
